import { rect as rectPts, type Pt } from '../card/art/geom';
import { hazardBands } from '../card/art/hatch';

/**
 * **濒死横带的斜杠。纯几何，不用贴图。**
 *
 * 一条横带、一百多个小四边形，每帧重算也不贵（实测可以忽略）。
 * 换来的是：没有 tile 接缝、没有贴图相位要维护、没有采样模糊 ——
 * 和牌面的网点/条纹是同一套做法（见 `card/art/hatch.ts` 的说明）。
 *
 * ## 为什么要分左右两半
 *
 * 评审要的是"横杠不断**从画面中心向左右两边**移动"。一条整体的横带做不到：
 * 整体平移只有一个方向。所以横带在中心切开，两半各自朝外跑。
 *
 * ## 两半之间只差两件事：相位符号、以及镜像还是平移
 *
 * 右半边一律是**左半边的相位取反**（相位增 = 沿法线往右下走，
 * 取反之后就往左上走 —— 于是两半的位移方向相反，都朝外）。
 *
 * 剩下一个问题：**斜杠的倾斜方向**。
 *
 * - `mirror = true`：右半边是左半边的**镜像**。两半像"人"字一样对称，
 *   斜杠的倾斜方向也相反（`/` 对 `\`）。中心那一刀看起来是连续的 V 形
 * - `mirror = false`（默认）：右半边是左半边的**平移**。这时两边的斜杠
 *   **倾斜方向完全相同**（都是 `/`），只有移动方向相反。中心会有一处错位
 *   （两半往相反方向跑，本来就不可能处处对齐）
 *
 * 评审要的是后者，但明说了"怕反悔"，所以做成开关。
 */
export interface BandRuns {
  readonly left: readonly (readonly Pt[])[];
  readonly right: readonly (readonly Pt[])[];
}

/**
 * @param width  横带的总宽（逻辑像素，铺满画面）
 * @param height 横带的高
 * @param phase  相位。随时间线性增长，条纹就动起来了
 * @param mirror 右半边是否镜像左半边。默认 false = 平移（斜杠倾斜方向相同）
 */
export function bandRuns(
  width: number,
  height: number,
  phase: number,
  pitch: number,
  duty: number,
  mirror = false,
): BandRuns {
  const half = width / 2;
  const shape = (p: number): Pt[][] => hazardBands(rectPts(0, 0, half, height), pitch, 45, duty, p);
  const left = shape(phase);

  // ⚠️ 相位是**在半个横带的局部坐标里**算的，右半边算完再整体平移 half。
  //    直接在 [half, width] 这个矩形上算也能得到同样的倾斜方向，但那样
  //    两半的相位基准不同（一个以 0 为原点、一个以 half 为原点），
  //    "平移"这件事就看不出来了 —— 现在这两半是**全等**的
  const right = mirror
    ? left.map((poly) => poly.map((p) => ({ x: width - p.x, y: p.y })))
    : shape(-phase).map((poly) => poly.map((p) => ({ x: p.x + half, y: p.y })));

  return { left, right };
}
